import { useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { useChats } from '@/data/store';
import { useAuth } from '@/lib/auth';
import { useBackStep } from '@/lib/backStep';
import type { Chat } from '@/data/types';
import { ChatRow } from './ChatRow';
import { LabelSheet } from './Labels';
import {
  ArchiveIcon, BellIcon, BellOffIcon, CheckIcon, CloseIcon, MoreIcon, ReadIcon, TackIcon, TagIcon,
  UnarchiveIcon, UnreadIcon,
} from './Icons';
import { SheetOption, useOverlay } from './Overlay';

// Shared by the chat list and the Archived screen: the rows, long-press selection and
// the selection bar's actions (pin, mute, archive, read), as in WhatsApp.

// Select mode starts from "Select chats" under the ⋯ at the top of the list, as WhatsApp
// on iPhone; a long press opens a chat's menu instead (Gary, 15 Oct).
export function useSelection() {
  const [ids, setIds] = useState<string[]>([]);
  const [on, setOn] = useState(false);
  const selecting = on || ids.length > 0;
  const clear = () => { setIds([]); setOn(false); };
  // The phone's Back ends select mode and stays on the list (mobile review, 19 Oct).
  useBackStep('chatsSelect', selecting, clear);
  return {
    ids,
    selecting,
    start: () => setOn(true),
    toggle: (id: string) => setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])),
    /** Every chat in the list as it is filtered now, or none. */
    setAll: (all: string[]) => { setOn(true); setIds(all); },
    /** Nothing picked, still in select mode. */
    unpick: () => setIds([]),
    clear,
  };
}

type Selection = ReturnType<typeof useSelection>;

/** A row's chats: its own and, when it stands for a customer's WhatsApp and email chats, the other one too. */
function pairOf(c: Chat): string[] {
  return c.pairedId ? [c.id, c.pairedId] : [c.id];
}

/**
 * Select mode (Gary, 18 Oct: once chats were selected the menu was out of reach): Select all for the list as
 * filtered, the two everyday actions as icons, and every action in words under More.
 */
export function SelectionBar({ selection, chats, shown }: { selection: Selection; chats: Chat[]; shown: Chat[] }) {
  const { setPinned, setMuted, setArchived, setRead } = useChats();
  const { toast, openSheet, closeSheet } = useOverlay();
  // Only the chats shown as selected are acted on, so the count and the change always match (mobile review, 19 Oct:
  // a chat picked on another tab was archived too).
  const picked = chats.filter((c) => selection.ids.includes(c.id));
  const allPinned = picked.length > 0 && picked.every((c) => c.pinned);
  const allMuted = picked.length > 0 && picked.every((c) => c.muted);
  const allArchived = picked.length > 0 && picked.every((c) => c.archived);
  const anyUnread = picked.some((c) => c.unread > 0 || c.markedUnread);
  const n = picked.length;
  const everything = shown.length > 0 && shown.every((c) => selection.ids.includes(c.id));
  const done = (text: string) => { closeSheet(); toast(text); selection.clear(); };
  const chats_ = (k: number) => (k === 1 ? 'chat' : 'chats');

  // A row may stand for a customer's WhatsApp and email chats: every action covers both (Gary, 19 Oct; mobile
  // review, 19 Oct: archive, pin and mute only moved one, so the customer stayed in the list).
  const both = [...new Set(picked.flatMap(pairOf))];
  const read = () => { setRead(both, true); done(`${n} ${chats_(n)} marked read`); };
  const unread = () => { setRead(both, false); done(`${n} ${chats_(n)} marked unread`); };
  const pin = () => { setPinned(both, !allPinned); done(allPinned ? 'Unpinned' : `${n} ${chats_(n)} pinned`); };
  const mute = () => { setMuted(both, !allMuted); done(allMuted ? 'Alerts back on' : 'Alerts off. You will still see the unread count.'); };
  const archive = () => { setArchived(both, !allArchived); done(allArchived ? `${n} ${chats_(n)} back in Chats` : `${n} ${chats_(n)} archived`); };

  function more() {
    openSheet(
      <>
        <h2 className="chats-sheet-title">{n} {chats_(n)} selected</h2>
        <SheetOption icon={<ReadIcon />} title="Mark as read" sub="Clears their unread counts" onClick={read} />
        <SheetOption icon={<UnreadIcon />} title="Mark as unread" sub="Keeps them in Reply as a reminder" onClick={unread} />
        {!allArchived && <SheetOption icon={<TackIcon />} title={allPinned ? 'Unpin' : 'Pin'} sub={allPinned ? 'Back in date order' : 'Keeps them at the top of your list'} onClick={pin} />}
        <SheetOption icon={allMuted ? <BellIcon /> : <BellOffIcon />} title={allMuted ? 'Turn alerts on' : 'Turn alerts off'} sub={allMuted ? 'Your phone alerts you again' : 'New messages still show as unread'} onClick={mute} />
        <SheetOption icon={allArchived ? <UnarchiveIcon /> : <ArchiveIcon />} title={allArchived ? 'Unarchive' : 'Archive'} sub={allArchived ? 'Back to Chats, closed chats reopen' : 'Moves them to Archived'} onClick={archive} />
      </>,
    );
  }

  return (
    <header className="chats-topbar chats-topbar--select">
      <button type="button" className="chats-icon-btn" aria-label="Cancel selection" onClick={selection.clear}><CloseIcon /></button>
      <h1 className="chats-topbar-title">{n ? `${n} selected` : 'Select chats'}</h1>
      <button type="button" className="chats-select-all" onClick={() => selection.setAll(everything ? [] : shown.map((c) => c.id))}>{everything ? 'None' : 'All'}</button>
      {n > 0 && (
        <>
          <button type="button" className="chats-icon-btn" aria-label={anyUnread ? 'Mark as read' : 'Mark as unread'} title={anyUnread ? 'Mark as read' : 'Mark as unread'} onClick={anyUnread ? read : unread}>
            {anyUnread ? <ReadIcon /> : <UnreadIcon />}
          </button>
          <button type="button" className="chats-icon-btn" aria-label={allArchived ? 'Unarchive' : 'Archive'} title={allArchived ? 'Unarchive' : 'Archive'} onClick={archive}>
            {allArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
          </button>
          <button type="button" className="chats-icon-btn" aria-label="More actions" title="More actions" onClick={more}><MoreIcon /></button>
        </>
      )}
    </header>
  );
}

/**
 * The "More" sheet behind a left swipe, as on an iPhone, and behind a right-click or the
 * row's ⋮ on a computer, where it also carries Read and Pin (Gary, 15 Oct: no swipes there). Left out on purpose: Delete chat
 * (Gary, 14 Oct: delete is archive, Sentinel keeps the record), Clear chat, Export chat
 * and Lock chat (the app already sits behind the Sentinel login).
 */
// The sheet renders outside the chats provider, so it is handed the actions it needs.
interface MoreActions {
  live: boolean;
  headOffice: boolean;
  navigate: (to: string) => void;
  setMuted: (ids: string[], muted: boolean) => void;
  setArchived: (ids: string[], archived: boolean) => void;
  setRead: (ids: string[], read: boolean) => void;
  setPinned: (ids: string[], pinned: boolean) => void;
  closeChat: (id: string, outcome: 'booked' | 'closed') => void;
}

function MoreSheet({ chat: c, actions, onSelect }: { chat: Chat; actions: MoreActions; onSelect: () => void }) {
  const { setMuted, setArchived, setRead, setPinned } = actions;
  const { openSheet, closeSheet, toast } = useOverlay();
  const then = (fn: () => void) => () => { closeSheet(); fn(); };
  const unread = c.unread > 0 || !!c.markedUnread;
  // The everyday actions only, one line each, as WhatsApp (Gary, 18 Oct: 12 items was too long). Contact and
  // Ask Leo are the Info tabs; Reassign, Mark booked and Close chat are in Info under "Assigned to".
  return (
    <>
      <h2 className="chats-sheet-title">{c.name}</h2>
      <SheetOption icon={unread ? <ReadIcon /> : <UnreadIcon />} title={unread ? 'Mark as read' : 'Mark as unread'} sub="" onClick={then(() => setRead(pairOf(c), unread))} />
      {!c.archived && (
        <SheetOption icon={<TackIcon />} title={c.pinned ? 'Unpin' : 'Pin'} sub=""
          onClick={then(() => { setPinned(pairOf(c), !c.pinned); toast(c.pinned ? 'Unpinned' : 'Chat pinned'); })} />
      )}
      <SheetOption icon={c.muted ? <BellIcon /> : <BellOffIcon />} title={c.muted ? 'Turn alerts on' : 'Turn alerts off'} sub=""
        onClick={then(() => { setMuted(pairOf(c), !c.muted); toast(c.muted ? 'Alerts on for this chat' : 'Alerts off for this chat. New messages still show as unread.'); })} />
      <SheetOption icon={<TagIcon />} title="Labels" sub={c.labels?.length ? c.labels.map((l) => l.name).join(', ') : ''} onClick={() => openSheet(<LabelSheet chat={c} />)} />
      <SheetOption icon={c.archived ? <UnarchiveIcon /> : <ArchiveIcon />} title={c.archived ? 'Unarchive' : 'Archive'} sub=""
        onClick={then(() => { setArchived(pairOf(c), !c.archived); toast(c.archived ? 'Chat unarchived' : 'Archived: off your list, still open for the team'); })} />
      <SheetOption icon={<CheckIcon />} title="Select" sub="" onClick={then(onSelect)} />
    </>
  );
}

export function ChatRows({ chats, selection, showBrand, markOld }: { chats: Chat[]; selection: Selection; showBrand: boolean; markOld?: boolean | 'closed' }) {
  const navigate = useNavigate();
  // On a wide screen the open chat sits beside the list: its row is marked.
  const open = useMatch('/chat/:id/*')?.params.id;
  const { access } = useAuth();
  const { live, setArchived, setRead, setPinned, setMuted, closeChat } = useChats();
  const { openSheet, toast } = useOverlay();
  const actions: MoreActions = { live, headOffice: access.role === 'head_office', navigate, setMuted, setArchived, setRead, setPinned, closeChat };
  return (
    <>
      {chats.map((c) => (
        <ChatRow
          key={c.id}
          chat={c}
          selecting={selection.selecting}
          selected={selection.ids.includes(c.id)}
          showBrand={showBrand}
          markOld={markOld}
          active={c.id === open || (!!open && c.pairedId === open)}
          onOpen={() => navigate(`/chat/${c.id}`)}
          onToggleSelect={() => selection.toggle(c.id)}
          onArchive={() => { setArchived(pairOf(c), !c.archived); toast(c.archived ? 'Chat unarchived' : 'Chat archived'); }}
          onToggleRead={() => setRead(pairOf(c), c.unread > 0 || !!c.markedUnread)}
          onTogglePin={() => { setPinned(pairOf(c), !c.pinned); toast(c.pinned ? 'Unpinned' : 'Chat pinned'); }}
          onMore={() => openSheet(<MoreSheet chat={c} actions={actions} onSelect={() => { selection.start(); selection.toggle(c.id); }} />)}
        />
      ))}
    </>
  );
}

/** Pinned first, then most recent, as WhatsApp orders the list. */
export function ordered(chats: Chat[]): Chat[] {
  return [...chats].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
}
